import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from "../context/AuthContext";
import { 
  Trophy, MapPin, CalendarBlank, CaretRight, Spinner, 
  Eye, EyeSlash, PencilSimple, X, CloudArrowUp, CheckCircle
} from "@phosphor-icons/react";

const API_BASE_URL = "https://warfc.com/api";

export default function PublicEventsPage() {
  const { user, token } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/events`);
      setEvents(res.data.events || res.data);
    } catch (err) { console.error(err); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEvents(); }, []);

  const categorized = useMemo(() => {
    const now = new Date();
    const visible = isAdmin ? events : events.filter(e => e.status === 'published');
    return {
      upcoming: visible.filter(e => new Date(e.date) >= now).sort((a,b) => new Date(a.date) - new Date(b.date)),
      past: visible.filter(e => new Date(e.date) < now).sort((a,b) => new Date(b.date) - new Date(a.date))
    };
  }, [events, isAdmin]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Spinner className="animate-spin text-cyan-500" size={48} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans pb-20">
      <div className="max-w-7xl mx-auto px-4 pt-20">
        
        {/* UPCOMING */}
        <section className="mb-24">
          <h1 className="text-6xl md:text-8xl font-black italic uppercase tracking-tighter mb-12">
            WAR<span className="text-cyan-500">FC</span> EVENTS
          </h1>
          <div className="grid grid-cols-1 gap-10">
            {categorized.upcoming.map((event, idx) => (
              <EventCard 
                key={event.id} 
                event={event} 
                isFeatured={idx === 0}
                isAdmin={isAdmin}
                onEdit={() => setEditingEvent(event)}
                onRefresh={fetchEvents}
                token={token}
              />
            ))}
          </div>
        </section>

        {/* PAST */}
        <section>
          <h2 className="text-2xl font-black uppercase italic tracking-widest mb-10 text-neutral-600 border-l-4 border-neutral-800 pl-4">Past Wars</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categorized.past.map(event => (
              <EventCard key={event.id} event={event} isAdmin={isAdmin} onEdit={() => setEditingEvent(event)} onRefresh={fetchEvents} token={token} />
            ))}
          </div>
        </section>
      </div>

      {editingEvent && (
        <EditModal 
          event={editingEvent} 
          token={token} 
          onClose={() => setEditingEvent(null)} 
          onRefresh={fetchEvents} 
        />
      )}
    </div>
  );
}

function EventCard({ event, isFeatured, isAdmin, onEdit, onRefresh, token }) {
  const isDraft = event.status === 'draft';
  const isToday = new Date(event.date).toDateString() === new Date().toDateString();

  // FIX: This matches your Home.jsx logic
  const getPosterURL = (path) => {
    if (!path) return "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=2000&auto=format&fit=crop";
    
    // Remove leading slash if it exists
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    
    // Return the full API path
    return `https://warfc.com/api/${cleanPath}`;
  };

  const poster = getPosterURL(event.image_url);

  const toggleStatus = async (e) => {
    e.preventDefault();
    const newStatus = isDraft ? 'published' : 'draft';
    await axios.patch(`${API_BASE_URL}/events/${event.id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
    onRefresh();
  };

  return (
    <div className={`relative group overflow-hidden rounded-[2rem] border transition-all duration-500 ${isToday ? 'border-red-600 shadow-[0_0_30px_rgba(220,38,38,0.2)]' : 'border-white/10'}`}>
      
      {isAdmin && (
        <div className="absolute top-6 left-6 z-30 flex gap-2">
          <button onClick={toggleStatus} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 backdrop-blur-md border ${isDraft ? 'bg-yellow-500/80 border-yellow-500 text-black' : 'bg-black/60 border-green-500 text-green-500'}`}>
            {isDraft ? <EyeSlash weight="bold" /> : <Eye weight="bold" />} {event.status}
          </button>
          <button onClick={(e) => { e.preventDefault(); onEdit(); }} className="bg-black/60 border border-white/20 p-2 rounded-xl hover:bg-cyan-500 transition-colors">
            <PencilSimple weight="bold" />
          </button>
        </div>
      )}

      <Link to={`/events/${event.id}/fighters`} className="block relative">
        <div className="absolute inset-0 z-0 transition-transform duration-700 group-hover:scale-105">
          <img src={poster} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        </div>

        <div className={`relative z-10 flex flex-col justify-end ${isFeatured ? 'min-h-[500px] p-12' : 'min-h-[300px] p-8'}`}>
          {isToday && <span className="absolute top-6 right-6 flex items-center gap-2 bg-red-600 px-3 py-1 rounded text-[10px] font-black animate-pulse">LIVE NOW</span>}
          
          <div className="flex items-center gap-4 mb-4">
            <span className="text-cyan-500 text-[10px] font-black uppercase tracking-widest">{event.ruleSet}</span>
            <span className="text-white/50 text-[10px] font-black uppercase">{new Date(event.date).toDateString()}</span>
          </div>
          <h3 className={`${isFeatured ? 'text-5xl md:text-7xl' : 'text-2xl'} font-black uppercase italic leading-[0.85] mb-4`}>{event.name}</h3>
          <div className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase italic"><MapPin size={16} className="text-red-600"/> {event.location}</div>
        </div>
      </Link>
    </div>
  );
}

function EditModal({ event, token, onClose, onRefresh }) {
  const [formData, setFormData] = useState({ ...event });
  const [loading, setLoading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    await axios.patch(`${API_BASE_URL}/events/${event.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
    onRefresh(); onClose();
  };

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    const data = new FormData();
    data.append('poster', file);
    setLoading(true);
    const res = await axios.post(`${API_BASE_URL}/events/${event.id}/upload`, data, { 
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } 
    });
    setFormData({ ...formData, image_url: res.data.imageUrl });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-md">
      <div className="bg-[#111] border border-white/10 w-full max-w-xl rounded-[2rem] overflow-hidden">
        <div className="p-8 flex justify-between items-center border-b border-white/5">
          <h2 className="text-xl font-black uppercase italic text-cyan-500">Edit War</h2>
          <button onClick={onClose}><X size={24}/></button>
        </div>
        <form onSubmit={save} className="p-8 space-y-6">
          <div className="h-40 bg-white/5 rounded-2xl border-2 border-dashed border-white/10 flex flex-col items-center justify-center relative overflow-hidden">
            {formData.image_url ? <img 
  src={formData.image_url ? `https://warfc.com/api/${formData.image_url.startsWith('/') ? formData.image_url.slice(1) : formData.image_url}` : ""} 
  className="absolute inset-0 w-full h-full object-cover opacity-40" 
/> : null}
            <label className="relative z-10 flex flex-col items-center cursor-pointer">
              <CloudArrowUp size={32}/>
              <span className="text-[10px] font-bold mt-2 uppercase">{loading ? 'Uploading...' : 'Change Poster'}</span>
              <input type="file" className="hidden" onChange={uploadImage} />
            </label>
          </div>
          <input className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-sm" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Event Name" />
          <div className="grid grid-cols-2 gap-4">
            <input className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-sm" type="date" value={formData.date?.split('T')[0]} onChange={e => setFormData({...formData, date: e.target.value})} />
            <input className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-sm" value={formData.ruleSet} onChange={e => setFormData({...formData, ruleSet: e.target.value})} placeholder="Rules (e.g. K-1)" />
          </div>
          <button type="submit" className="w-full bg-cyan-600 py-4 rounded-xl font-black uppercase italic">Update Event</button>
        </form>
      </div>
    </div>
  );
}